import { BrandIcon } from "@ui/BrandIcon";
import { Button } from "@ui/Button";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import {
	type useVolumeControl,
	VolumeControl,
} from "@ui/molecules/VideoPreview/VolumeControl";
import { HStack, VStack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { Text } from "@ui/typography/Text";
import {
	type Accessor,
	createEffect,
	createSignal,
	type JSX,
	onCleanup,
	Show,
} from "solid-js";
import type { VideoEvidence } from "../../ChatEventTypes";
import { videoMentionFromClip } from "../../utils/videoEvidenceMentions";
import {
	addVideoMentionToClipboard,
	isVideoMentionInClipboard,
} from "../clipboard/clipboardStore";
import { useOffersClipboard } from "../clipboard/offersClipboard";
import { mentionFromVideoEvidence } from "../mentionUtils";
import { dispatchProfileMentionPanel } from "../profilePanelEvents";
import { ReportVideoButton } from "../report/ReportVideoButton";
import { CarouselVideoProgress } from "./CarouselVideoProgress";
import { ClipTiming } from "./ClipTiming";
import { applyNavClearance } from "./navClearance";
import { platformLinkForVideoEvidence } from "../../utils/platformPostUrl";
import { shareOrDownloadClip } from "./shareClip";
import { VideoAvatarPill } from "./VideoAvatarPill";
import "./VideoCarouselOverlay.css";

type Props = {
	boardId?: string;
	activeSlideIndex: number;
	videoEvidence: VideoEvidence[];
	currentVideoProgressPercent: number;
	isPlaying: boolean;
	onTogglePlay: () => void;
	/** Moves the active clip's playhead, in seconds from the clip's start. */
	onSeek: (clipRelativeSeconds: number) => void;
	onSelectVideo: (index: number) => void;
	onEditClip?: () => void;
	onClose: () => void;
	volume: ReturnType<typeof useVolumeControl>;
	headerStart?: JSX.Element;
	onAddToBoard?: () => void;
	clipInBoard?: Accessor<boolean>;
	membershipLoading?: Accessor<boolean>;
};

/**
 * Overlay to display the video details.
 */
export const VideoCarouselOverlay = (props: Props) => {
	const [sharing, setSharing] = createSignal(false);
	const offersClipboard = useOffersClipboard();
	const activeClip = () => props.videoEvidence[props.activeSlideIndex];
	const hasHeaderLeading = () =>
		props.headerStart != null || props.onEditClip != null;
	const membershipLoading = () => props.membershipLoading?.() ?? false;
	const inBoard = () =>
		!membershipLoading() && (props.clipInBoard?.() ?? false);
	const activeMention = () => {
		const clip = activeClip();
		return clip
			? videoMentionFromClip(clip, props.activeSlideIndex + 1)
			: undefined;
	};
	const inClipboard = () => {
		const mention = activeMention();
		return mention ? isVideoMentionInClipboard(mention) : false;
	};

	const platformUrl = () => platformLinkForVideoEvidence(activeClip());

	// Keep the action column clear of the carousel's chevrons as the panel is
	// resized. The overlay covers the viewport the chevrons are centred on, so
	// its own box is all the geometry the clearance needs.
	const [overlayEl, setOverlayEl] = createSignal<HTMLDivElement>();
	createEffect(() => {
		const overlay = overlayEl();
		const obstructed = props.videoEvidence.length > 1;
		if (!overlay) {
			return;
		}
		const observer = new ResizeObserver(() => {
			applyNavClearance(overlay, obstructed);
		});
		observer.observe(overlay);
		onCleanup(() => observer.disconnect());
	});

	const handleInfluencerClick = (event: MouseEvent) => {
		event.stopPropagation();
		const clip = activeClip();
		if (!clip) {
			return;
		}
		const mention = mentionFromVideoEvidence(
			clip.influencer,
			clip.social_account,
		);
		if (mention) {
			dispatchProfileMentionPanel(mention);
		}
	};

	const handleShare = async (event: MouseEvent) => {
		event.stopPropagation();
		const clip = activeClip();
		if (!clip?.video_id || sharing()) {
			return;
		}

		setSharing(true);
		try {
			await shareOrDownloadClip(clip, props.boardId);
		} catch (error) {
			console.error("Failed to share or download clip:", error);
		} finally {
			setSharing(false);
		}
	};

	return (
		<Show when={activeClip()}>
			{(clip) => (
				<div class="board-video-overlay__content" ref={setOverlayEl}>
					<button
						type="button"
						class="circle-icon large-play-icon board-video-overlay__play-icon"
						aria-label={props.isPlaying ? "Pause" : "Play"}
						onClick={(e) => {
							e.stopPropagation();
							props.onTogglePlay();
						}}
					>
						<MaterialSymbol
							symbol={props.isPlaying ? "pause" : "play_arrow"}
							relativeSize={2}
						/>
					</button>
					<VStack justify="space-between" style={{ height: "100%" }}>
						<VStack spacing="medium" class="board-video-overlay__header">
							<CarouselVideoProgress
								videoIndex={props.activeSlideIndex}
								videoEvidence={props.videoEvidence}
								currentVideoProgressPercent={props.currentVideoProgressPercent}
								onSelectVideo={props.onSelectVideo}
							/>
							<HStack
								justify={hasHeaderLeading() ? "space-between" : "end"}
								fullWidth
							>
								<Show
									when={props.headerStart}
									fallback={
										<Show when={props.onEditClip}>
											<TooltipTrigger>
												<Button
													aria-label="Edit clip"
													theme="black"
													shape="circle"
													size="large"
													symbol="settings"
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();
														props.onEditClip?.();
													}}
												/>
												<Tooltip>Edit clip</Tooltip>
											</TooltipTrigger>
										</Show>
									}
								>
									{props.headerStart}
								</Show>
								<Button
									aria-label="Close"
									theme="black"
									shape="circle"
									size="large"
									symbol="close"
									onClick={(e) => {
										e.stopPropagation();
										props.onClose();
									}}
								/>
							</HStack>
						</VStack>
						<VStack spacing="large" class="board-video-overlay__footer">
							<HStack justify="space-between" align="end">
								<VideoAvatarPill
									influencer={clip().influencer}
									duration={clip().clip_duration}
									viewCount={clip().view_count}
									onInfluencerClick={
										mentionFromVideoEvidence(
											clip().influencer,
											clip().social_account,
										)
											? handleInfluencerClick
											: undefined
									}
								/>
								<VStack spacing="medium" class="board-video-overlay__actions">
									<Show when={offersClipboard()}>
										<button
											type="button"
											class="circle-icon-button"
											aria-label={
												inClipboard() ? "On clipboard" : "Add to clipboard"
											}
											onClick={(e) => {
												e.stopPropagation();
												const mention = activeMention();
												if (mention) {
													addVideoMentionToClipboard(mention);
												}
											}}
										>
											<div class="circle-icon">
												<MaterialSymbol
													symbol={inClipboard() ? "check" : "add"}
												/>
											</div>
											<Text color="white" size="small">
												Clipboard
											</Text>
										</button>
									</Show>
									<Show when={props.onAddToBoard}>
										<button
											type="button"
											class="circle-icon-button"
											classList={{ "in-board": inBoard() }}
											disabled={membershipLoading()}
											aria-label={
												membershipLoading()
													? "Loading board status"
													: inBoard()
														? "Update board clip"
														: "Add to board"
											}
											onClick={(e) => {
												e.stopPropagation();
												if (membershipLoading()) {
													return;
												}
												props.onAddToBoard?.();
											}}
										>
											<div class="circle-icon">
												<MaterialSymbol
													symbol={
														membershipLoading()
															? "progress_activity"
															: inBoard()
																? "bookmark_added"
																: "bookmark_add"
													}
													color={inBoard() ? "inherit" : "white"}
													filled={inBoard()}
													class={
														membershipLoading() ? "animate-spin" : undefined
													}
												/>
											</div>
											<Text color="white" size="small">
												Board
											</Text>
										</button>
									</Show>
									<button
										type="button"
										class="circle-icon-button"
										aria-label="Share clip"
										disabled={sharing()}
										onClick={handleShare}
									>
										<div class="circle-icon">
											<MaterialSymbol symbol="ios_share" />
										</div>
										<Text color="white" size="small">
											Share
										</Text>
									</button>
									<ReportVideoButton videoId={clip().video_id} />
									<Show when={platformUrl()}>
										{(url) => (
											<button
												type="button"
												class="circle-icon-button"
												aria-label="Open on platform"
												onClick={(e) => {
													e.stopPropagation();
													window.open(
														url(),
														"_blank",
														"noopener,noreferrer",
													);
												}}
											>
												<div class="circle-icon">
													<BrandIcon
														fillWithCurrentColor={true}
														platform={
															activeClip()?.social_account?.platform ?? ""
														}
														width="1rem"
														height="1rem"
													/>
												</div>
												<Text color="white" size="small">
													Platform
												</Text>
											</button>
										)}
									</Show>
								</VStack>
							</HStack>
							<Text as="h3" color="white" class="board-video-overlay__title">
								{clip().title}
							</Text>
							<HStack spacing="small" justify="space-between">
								<ClipTiming
									clip={clip()}
									playbackProgressPercent={props.currentVideoProgressPercent}
									onSeek={props.onSeek}
								/>
								<VolumeControl controls={props.volume} />
							</HStack>
						</VStack>
					</VStack>
				</div>
			)}
		</Show>
	);
};
